import { Box, Flex, FlexProps, Icon, Spinner } from "@chakra-ui/react";
import React from "react";

import { VideoDisplayMode } from "../../types";
import { CallbackRef, MediaListeners } from "../MediaPlayer/useMediaPlayer";
import { VideoCallbackRef } from "../MediaPlayer/useVideoState";
import { IconNoVideo } from "./theme";
import VideoPlayerV2 from "./VideoPlayerV2";

export type PlayerWithDisplayModeMessageV2Props = {
  clipInProgress: boolean;
  clipTimedOut: boolean;
  videoDisplayMode: VideoDisplayMode;
  mediaSrc?: string;
  listeners: MediaListeners;
  mediaPlayerRef: CallbackRef;
  videoRef: VideoCallbackRef;
  height: string | number;
  isVideoVisible: boolean;
  pipActive?: boolean;
  currentTime?: number;
  cameraTimestamps?: Array<{ timestamp: number; enabled: boolean }>;
  onClick?: FlexProps["onClick"];
  /** Callback to exit PiP mode and return video to the main player */
  onExitPip?: () => void;
  /** CSS object-fit for the video element (e.g., "cover" for no black bars) */
  objectFit?: React.CSSProperties["objectFit"];
};

const PlayerWithDisplayModeMessageV2: React.FC<
  PlayerWithDisplayModeMessageV2Props
> = ({
  clipInProgress,
  clipTimedOut,
  videoDisplayMode,
  mediaSrc,
  listeners,
  mediaPlayerRef,
  videoRef,
  height,
  isVideoVisible,
  pipActive,
  currentTime,
  cameraTimestamps,
  onClick,
  onExitPip,
  objectFit,
}) => {
  if (clipTimedOut) {
    return (
      <MessageFrame
        message="Clip creation timed out. Please try again."
        icon={<Icon as={IconNoVideo} color="whiteAlpha.600" boxSize={12} />}
      />
    );
  }
  if (clipInProgress) {
    return (
      <MessageFrame
        message="Processing clip..."
        icon={<Spinner width="48px" height="48px" color="whiteAlpha.600" />}
      />
    );
  }
  if (videoDisplayMode === VideoDisplayMode.VIDEO) {
    return (
      <VideoPlayerV2
        mediaPlayerRef={mediaPlayerRef}
        videoRef={videoRef}
        src={mediaSrc}
        height={height}
        isVideoVisible={isVideoVisible}
        pipActive={pipActive}
        currentTime={currentTime}
        cameraTimestamps={cameraTimestamps}
        onClick={onClick}
        onExitPip={onExitPip}
        objectFit={objectFit}
        {...listeners}
      />
    );
  }
  if (videoDisplayMode === VideoDisplayMode.AUDIO) {
    return <audio ref={mediaPlayerRef} src={mediaSrc} {...listeners} />;
  }
  if (videoDisplayMode === VideoDisplayMode.PROCESSING) {
    return (
      <MessageFrame
        message="Processing recording..."
        icon={<Spinner width="48px" height="48px" color="whiteAlpha.600" />}
      />
    );
  }
  if (videoDisplayMode === VideoDisplayMode.IN_PROGRESS) {
    return (
      <MessageFrame
        message="Interview is in progress..."
        icon={<Spinner width="48px" height="48px" color="whiteAlpha.600" />}
      />
    );
  }
  if (videoDisplayMode === VideoDisplayMode.NO_RECORDING) {
    return (
      <MessageFrame
        message="No recording available."
        icon={<Icon as={IconNoVideo} color="whiteAlpha.600" boxSize={12} />}
      />
    );
  }
  return null;
};

type MessageFrameProps = {
  message: string;
  icon: React.ReactNode;
};

const MessageFrame: React.FC<MessageFrameProps> = ({ message, icon }) => {
  return (
    <Flex
      align="center"
      flexDirection="column"
      h="220px"
      justify="center"
      py={{ base: 4, lg: 8 }}
      m="auto"
    >
      {icon}
      <Box color="whiteAlpha.600" fontWeight="bold" fontSize="md" mt="1">
        {message}
      </Box>
    </Flex>
  );
};

export default PlayerWithDisplayModeMessageV2;
